if (!customElements.get('custom-drawer')) {
  customElements.define('custom-drawer', class CustomDrawer extends HTMLElement {
    constructor() {
      super();
      // Generate a unique ID if none is provided
      if (!this.getAttribute('id')) {
        this.setAttribute('id', `drawer-${Math.random().toString(36).substr(2, 9)}`);
      }

      // Get the section ID from data attribute
      this.sectionId = this.dataset.drawerId;
      
      // Store original scroll position for iOS
      this.scrollPosition = 0;
      
      // Bind touch event handlers for iOS
      this.handleTouchMove = this.handleTouchMove.bind(this);
      
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      
      // Add click event listener to the entire drawer area
      this.addEventListener('click', (evt) => {
        // Close only if clicking the overlay (background) area
        if (evt.target === this) {
          this.close();
        }
      });

      // Add global click listener for drawer triggers
      document.addEventListener('click', (evt) => {
        const trigger = evt.target.closest('[data-drawer-trigger]');
        if (trigger) {
          this.open(trigger);
        }
      });
      
      // Move drawer to body when constructed
      this.moveToBody();
    }
    
    // New method to move drawer to body
    moveToBody() {
      // Only move if not already at body level
      if (this.parentElement && this.parentElement.tagName !== 'BODY' && !this.parentElement.classList.contains('custom-drawer-container')) {
        // Create container if it doesn't exist
        let container = document.querySelector('body > .custom-drawer-container');
        if (!container) {
          container = document.createElement('div');
          container.classList.add('custom-drawer-container');
          document.body.appendChild(container);
        }
        
        // Move this drawer to the container
        container.appendChild(this);
      }
    }

    // Handle touch events to prevent scrolling on iOS
    handleTouchMove(event) {
      // Allow scrolling within the drawer content
      const drawerContent = this.querySelector('.drawer-container');
      if (drawerContent && drawerContent.contains(event.target)) {
        return; // Allow scrolling within drawer
      }
      
      // Prevent scrolling on the background/overlay
      event.preventDefault();
    }

    // Enhanced scroll prevention for iOS
    preventScroll() {
      // Store current scroll position
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      
      // Add overflow hidden to body
      document.body.classList.add('overflow-hidden');
      
      // For iOS: Add fixed positioning to prevent elastic scrolling
      if (this.isIOS()) {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.style.width = '100%';
        
        // Prevent touch events on body
        document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      }
    }

    // Enhanced scroll restoration for iOS
    restoreScroll() {
      // Remove overflow hidden from body
      document.body.classList.remove('overflow-hidden');
      
      // For iOS: Restore normal positioning and scroll position
      if (this.isIOS()) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        
        // Restore scroll position
        window.scrollTo(0, this.scrollPosition);
        
        // Remove touch event listener
        document.removeEventListener('touchmove', this.handleTouchMove);
      }
    }

    // Detect iOS devices
    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
             (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    open(triggeredBy) {
      // Ensure drawer is at body level before opening
      this.moveToBody();
      
      // Only open if the trigger belongs to this section
      if (triggeredBy && triggeredBy.closest(`[data-section-id="${this.sectionId}"]`)) {
        this.setActiveElement(triggeredBy);
        
        requestAnimationFrame(() => {
          this.classList.add('animate');
          requestAnimationFrame(() => {
            this.classList.add('active');
          });
        });

        // Use enhanced scroll prevention
        this.preventScroll();
      }
    }

    close() {
      this.classList.remove('active');
      this.addEventListener('transitionend', () => {
        this.classList.remove('animate');
      }, { once: true });
      
      // Use enhanced scroll restoration
      this.restoreScroll();
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  });
}